<template>
  <loading-modal-widget></loading-modal-widget>
  <div class="app-wrapper" :class="{'app-publish': isPublishPath, 'app-auth': isAuthPath}">
    <router-view/>
  </div>
</template>

<script lang="ts">

import {defineComponent} from 'vue'
import LoadingModalWidget from '@shared/components/widgets/loading-modal.widget.vue'
import {
  cookies,
  getBaseDomain,
  getStoredToken,
  isDevelopmentEnv,
  websocket
} from "@shared/helpers";
import {redirectToSignIn} from "@shared/http-client";
import {CommonNs, WorkspaceNs} from "@shared/types";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {COOKIE_NAMES} from "@shared/constants/common.constants";
import {toast} from '@shared/ms-components'

export default defineComponent({
  name: 'App',
  components: {LoadingModalWidget},
  computed: {
    isPublishPath() {
      const path = window.location.pathname;
      return path.includes('/publish');
    },
    isAuthPath() {
      const path = window.location.pathname;
      return path.includes('/auth');
    }
  },
  unmounted() {
    websocket.disconnect();
  },
  mounted() {
    let redirectPath = String(window.location.pathname)
    redirectPath = redirectPath.substring(1)
    // If we are at loading page, do nothing
    if (!!redirectPath && redirectPath === 'loading') return
    if (!!redirectPath && redirectPath.includes('sign-up')) return
    if (!!redirectPath && redirectPath.includes('social')) return
    if (!!redirectPath && redirectPath.includes('reset-password')) return
    if (!!redirectPath && redirectPath.includes('forgot-password')) return
    if (!!redirectPath && redirectPath.includes('confirm-email')) return
    if (!!redirectPath && redirectPath.includes('confirm-delete-account')) return
    if (!!redirectPath && redirectPath.includes('confirm-delete-workspace')) return
    if (!!redirectPath && redirectPath.includes('publish')) return
    if (!!redirectPath && redirectPath.includes('auth-callback')) return

    const token = getStoredToken()
    if (!token || !token.accessToken) {
      redirectToSignIn('')
      cookies.set(COOKIE_NAMES.AFTER_SIGN_IN, JSON.stringify({
        name: window.location.pathname,
        search: window.location.search
      }), {domain: getBaseDomain})
      return
    }

    const workspaceStr = cookies.get(COOKIE_NAMES.WORKSPACE)
    if (!workspaceStr) {
      this.$router.push('/auth/existing-workspaces')
    } else {
      const workspace = JSON.parse(workspaceStr) as WorkspaceNs.SelectedWorkspaceInfo

      const domainName = window.location.host.split('.')[0]
      if (domainName !== 'localhost:5173' && domainName !== 'app' && domainName !== workspace.name) {
        // Different workspace selected
        this.$router.push('/auth/existing-workspaces')
      } else {
        let redirectStr = ''
        let queryParams = {}
        if (!!redirectPath && redirectPath !== '/' && redirectPath !== 'auth/sign-in') {
          if (window.location.search) {
            const additionalParams = String(window.location.search.slice(1))
            .split('&')
            .map(p => p.split('='))
            .reduce((obj, [key, value]) => ({...obj, [key]: value}), {})

            queryParams = {redirectTo: redirectPath, ...additionalParams}
          } else {
            queryParams = {redirectTo: redirectPath}
          }

          redirectStr = ''
          Object.entries(queryParams)
          .forEach(([key, value]) => {
            redirectStr = redirectStr.concat(`&${key}=${value}`)
          })

          redirectStr = '?' + redirectStr.slice(1)
        }

        if (isDevelopmentEnv) {
          if (redirectStr) {
            this.$router.push({name: 'Loading', query: queryParams})
          } else {
            this.$router.push({name: 'Loading'})
          }
        } else {
          const host = window.location.host
          if (host.split('.')[0] === workspace.name) {
            if (redirectStr) {
              this.$router.push({name: 'Loading', query: queryParams})
            } else {
              this.$router.push({name: 'Loading'})
            }
          } else {
            window.open(`https://${workspace.name}${getBaseDomain}/loading${redirectStr}`, '_self')
          }
        }

        websocket.wsConnectSubject.subscribe(() => {
          websocket.subscribe('/user/queue/toaster', (message: any) => {
            toast.error(message.body)
          })

          websocket.subscribe('/user/queue/message', (message: any) => {
            const messageBody = JSON.parse(message.body) as CommonNs.SocketEvent;
            console.log(messageBody);
            if (messageBody.type === CommonNs.SocketEventType.PACKAGE_CHANGE) {
              const workspaceStore = useWorkspaceStore();
              workspaceStore.loadWorkspace(workspaceStore.getSelectedWorkspaceId);
            }
          })

          websocket.subscribe(`/user/queue/onlineUsers`, (message: any) => {
            const workspaceStore = useWorkspaceStore();
            workspaceStore.onlineUserList = message.body
            console.log(message)
          })

        })
      }
    }
  }
})
</script>

<style lang="scss">
#app {
  height: 100%;
}

.app-wrapper {

  height: 100vh;

  &.app-publish {
    min-width: 100% !important;
    height: 100vh !important;
  }

  &.app-auth {
    min-width: unset !important;
  }
}
</style>
